import React from "react";
import { toast } from "react-toastify";
import { usePageTopNotification } from "src/components/reusable/components/PageTopNotification";
import { useApiRoutes } from "src/components/reusable/hooks/useApiRoutes";
import { useGlobalStore } from "src/components/reusable/hooks/useGlobalStore";
import { useRouterExt } from "src/components/reusable/hooks/useRouterExt";
import { findApiErrorMessage } from "src/components/reusable/utils/utils";
import { globalLanguages } from "src/shared/global_languages";
import { routes } from "src/shared/routes";
import { TAddOn } from "src/shared/types/globalStotre.types";
import { useTeamConfig } from "src/components/reusable/components/TeamConfigProvider";
import { useEventAttendance } from "./event-attendance/useEventAttendance";
import { normalizeRequiredAddOnSeatCounts } from "./utils.homepageFlow";

export function useHomePageFlow() {
  const router = useRouterExt();
  const { setGlobalState, events } = useGlobalStore();
  const { getUpcomingEventsApi } = useApiRoutes();
  const pageTopNotification = usePageTopNotification();
  const teamConfig = useTeamConfig();
  const { TEAM_GLOBAL_SETTINGS, HOME_PAGE_SETTINGS } = teamConfig;
  const { isReservationEnabled } = useEventAttendance();

  const handleGetUpcomingEvents = async () => {
    try {
      let queryParams = {};
      if (isReservationEnabled) queryParams = { sortBy: "opt_ins_first" };
      const apiEvents = await getUpcomingEventsApi.get({ queryParams });
      const optedInCountForEventReservation = apiEvents.optedInCountForEventReservation ?? null;
      const reservationLimitsByPass = apiEvents.reservationLimitsByPass ?? null;
      const events = normalizeRequiredAddOnSeatCounts(
        apiEvents.sortedEvents,
        [],
        teamConfig
      );
      setGlobalState({ events, optedInCountForEventReservation, reservationLimitsByPass });
    } catch (err) {
      toast.error(findApiErrorMessage(err));
    }
  };

  const handleRefreshEvents = async () => {
    try {
      let queryParams = {};
      if (isReservationEnabled) queryParams = { sortBy: "opt_ins_first" };
      const eventsResponse = await getUpcomingEventsApi.get({ queryParams });
      const optedInCountForEventReservation = eventsResponse.optedInCountForEventReservation ?? null;
      const reservationLimitsByPass = eventsResponse.reservationLimitsByPass ?? null;
      let apiEvents = eventsResponse.sortedEvents;

      // sorting events if they have inventory group
      apiEvents = apiEvents.map((event) => ({
        ...event,
        addOns: event.addOns.sort((a, b) => {
          if (a.inventoryGroup && b.inventoryGroup) {
            return a.inventoryGroup.localeCompare(b.inventoryGroup);
          }
          if (a.inventoryGroup) return -1;
          if (b.inventoryGroup) return 1;
          return 0;
        }),
      }));

      apiEvents = apiEvents.map((apiEvent) => {
        const stateEvt = events.find(evt => evt.eventCode === apiEvent.eventCode); // prettier-ignore
        if (!stateEvt) return apiEvent;

        apiEvent.addOns.map((apiAddOn) => {
          const stateAddOn = stateEvt.addOns.find(addOn => addOn.productId === apiAddOn.productId); // prettier-ignore
          if (!stateAddOn) return apiAddOn;
          if (stateAddOn.seatCount > 0) {
            apiAddOn.seatCount = stateAddOn.seatCount;
          }
          return apiAddOn;
        });

        return apiEvent;
      });

      setGlobalState({
        events: normalizeRequiredAddOnSeatCounts(apiEvents, events, teamConfig),
        optedInCountForEventReservation,
        reservationLimitsByPass,
      });
    } catch (err) {
      toast.error(findApiErrorMessage(err));
    }
  };

  const onChangeAddOnSeatCount = (newAddOn: TAddOn) => {
    const updatedEvents = events.map((event) => {
      if (event.eventCode === newAddOn.gameEventCode) {
        const updatedAddOns = event.addOns.map((addOn) => {
          if (addOn.productId === newAddOn.productId) return newAddOn;
          return addOn;
        });
        return { ...event, addOns: updatedAddOns };
      }
      return event;
    });
    setGlobalState({
      events: normalizeRequiredAddOnSeatCounts(
        updatedEvents,
        updatedEvents,
        teamConfig
      ),
    });
  };

  const setAddOnPurchaseSuccessMessage = () => {
    const Title =
      HOME_PAGE_SETTINGS?.ADD_ON?.ADD_ON_PURCHASE_SUCCESS_MESSAGE?.TITLE ??
      globalLanguages.ADD_ON_PURCHASE_SUCCESS_MESSAGE.TITLE;
    const Description =
      HOME_PAGE_SETTINGS?.ADD_ON?.ADD_ON_PURCHASE_SUCCESS_MESSAGE
        ?.DESCRIPTION ??
      globalLanguages.ADD_ON_PURCHASE_SUCCESS_MESSAGE.DESCRIPTION;

    pageTopNotification.set({
      isOpen: true,
      route: routes.home,
      title: <Title />,
      description: <Description />,
      backgroundColor:
        HOME_PAGE_SETTINGS?.DEFAULT_PAGE_TOP_NOTIFICATION?.BACKGROUND_COLOR,
    });
  };

  const handleAddOnPurchaseSuccess = async (
    checkoutRes: any,
    route?: string,
  ) => {
    if (route) return await router.pushSchema({ pathname: route });

    setAddOnPurchaseSuccessMessage();
    await handleGetUpcomingEvents();

    if (TEAM_GLOBAL_SETTINGS?.ENABLE_ADD_ON_PURCHASE_SUCCESS_PAGE)
      return await router.pushSchema({
        pathname: routes.addons_success_purchase,
        query: {
          orderNumber: checkoutRes?.orderNumber,
        },
      });

    await router.pushSchema({ pathname: routes.home });
  };

  const clearAddOnsCart = () => {
    const updatedEvents = events.map((event) => {
      const addOns = event.addOns.map((ad) => ({ ...ad, seatCount: 0 }));
      return { ...event, addOns };
    });
    setGlobalState({ events: updatedEvents });
  };

  return {
    handleGetUpcomingEvents,
    onChangeAddOnSeatCount,
    handleAddOnPurchaseSuccess,
    handleRefreshEvents,
    clearAddOnsCart,
  };
}
